import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { style } from 'typestyle';
import * as styles from '../statistics-tabs.styles';
import { IsMobileContext } from '../../../../core/context/is-mobile';
import { Center } from '../../../../core/components/center/center.component';
import ActivityIndicator from '../../../../core/components/activity-indicator/activity-indicator.component';
import StyleConstants from '../../../../core/styles.constants';
import Grid from '../../../../core/components/grid/grid.component';
import { useDashboardOverview } from '../hooks/dashboard.hook';
import Gauge from '../components/gauge.component';
import { type OfficeOverview } from '../models/dashboard.models';

interface Props {
  readonly onSelectOffice: (officeId: string) => void;
  readonly dataVersion?: number;
}

const OverviewTab = ({ onSelectOffice, dataVersion }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const isMobile = useContext(IsMobileContext);
  const { initialized, overview, error } = useDashboardOverview(undefined, dataVersion);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOffices = useMemo(() => {
    const allOffices = overview?.offices ?? [];
    const trimmedQuery = searchQuery.trim().toLowerCase();
    return trimmedQuery ? allOffices.filter((office) => office.name.toLowerCase().includes(trimmedQuery)) : allOffices;
  }, [overview, searchQuery]);

  if (!initialized) {
    return (
      <Center>
        <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
      </Center>
    );
  }

  if (error || !overview) {
    return <span className={style(styles.errorMessage)}>{t('dashboard-overview-error-message')}</span>;
  }

  return (
    <div>
      <div className={style(styles.kpiRow)}>
        <div className={style(styles.kpiCard)}>
          <div className={style(styles.kpiLabel)}>{t('dashboard-kpi-reserved')}</div>
          <div className={style(styles.kpiValue)}>{overview.totals.reserved}</div>
        </div>
        <div className={style(styles.kpiCard)}>
          <div className={style(styles.kpiLabel)}>{t('dashboard-kpi-checked-in')}</div>
          <div className={style(styles.kpiValue)}>{overview.totals.checkedIn}</div>
        </div>
        <div className={style(styles.kpiCard)}>
          <div className={style(styles.kpiLabel)}>{t('dashboard-kpi-show-up-rate')}</div>
          <div className={style(styles.kpiValue)}>{overview.totals.showUpRatePercentage.toFixed(1)}%</div>
        </div>
        <div className={style(styles.kpiCard)}>
          <div className={style(styles.kpiLabel)}>{t('dashboard-kpi-offices-over-threshold')}</div>
          <div className={style(overview.totals.officesOverThreshold > 0 ? styles.kpiValueWarn : styles.kpiValue)}>
            {overview.totals.officesOverThreshold}
          </div>
        </div>
      </div>

      <div className={style(styles.searchRow)}>
        <input
          type="text"
          className={style(styles.searchInput)}
          placeholder={t('search-by-name').toString()}
          value={searchQuery}
          onChange={(event): void => setSearchQuery(event.target.value)}
        />
      </div>

      {overview.offices.length === 0 && <span className={style(styles.emptyMessage)}>{t('no-offices-available')}</span>}

      {overview.offices.length > 0 && filteredOffices.length === 0 && (
        <span className={style(styles.emptyMessage)}>{t('dashboard-overview-no-search-results')}</span>
      )}

      {filteredOffices.length > 0 && (
        <Grid
          rowClass={style({ marginTop: 12 })}
          itemSpacing={16}
          numberOfColumns={3}
          numberOfItems={filteredOffices.length}
          renderItem={(index): React.ReactNode => {
            const office: OfficeOverview = filteredOffices[index];
            return (
              <div
                key={office.officeId}
                role="button"
                tabIndex={0}
                className={style(styles.officeCard(isMobile))}
                onClick={(): void => onSelectOffice(office.officeId)}
                onKeyDown={(event): void => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    onSelectOffice(office.officeId);
                  }
                }}
              >
                <div className={style(styles.officeCardHeader)}>
                  <div className={style(styles.officeName)}>{office.name}</div>
                  <span className={style(styles.statusBadge(office.status))}>{t(`dashboard-status-${office.status}`)}</span>
                </div>
                <div className={style(styles.gaugeWrap)}>
                  <Gauge
                    value={office.occupancyPercentage}
                    thresholdMin={office.thresholdMinPercentage}
                    thresholdMax={office.thresholdMaxPercentage}
                  />
                </div>
                <div className={style(styles.miniStats)}>
                  <div className={style(styles.miniStat)}>
                    <span className={style(styles.miniStatValue)}>{office.reserved}</span>
                    <span className={style(styles.miniStatLabel)}>{t('dashboard-kpi-reserved')}</span>
                  </div>
                  <div className={style(styles.miniStat)}>
                    <span className={style(styles.miniStatValue)}>{office.checkedIn}</span>
                    <span className={style(styles.miniStatLabel)}>{t('dashboard-kpi-checked-in')}</span>
                  </div>
                  <div className={style(styles.miniStat)}>
                    <span className={style(styles.miniStatValue)}>
                      {office.thresholdMinPercentage}–{office.thresholdMaxPercentage}%
                    </span>
                    <span className={style(styles.miniStatLabel)}>{t('dashboard-threshold-label')}</span>
                  </div>
                </div>
              </div>
            );
          }}
        />
      )}
    </div>
  );
};

export default OverviewTab;
